import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, Users, FileText, Receipt, Calculator, ListChecks, Wallet, Settings, LogOut, Menu, Sparkles, CreditCard, AlertTriangle,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, key: "dashboard" },
  { to: "/schedule", label: "Schedule", icon: CalendarDays, key: "schedule" },
  { to: "/clients", label: "Clients", icon: Users, manager: true, key: "clients" },
  { to: "/estimates", label: "Estimates", icon: FileText, manager: true, key: "estimates" },
  { to: "/invoices", label: "Invoices", icon: Receipt, manager: true, key: "invoices" },
  { to: "/calculator", label: "Price calculator", icon: Calculator, manager: true, key: "calculator" },
  { to: "/checklists", label: "Checklists", icon: ListChecks, manager: true, key: "checklists" },
  { to: "/expenses", label: "Expenses", icon: Wallet, manager: true, key: "expenses" },
  { to: "/settings", label: "Settings", icon: Settings, manager: true, key: "settings" },
  { to: "/billing", label: "Billing", icon: CreditCard, manager: true, key: "billing" },
];
const MOBILE_KEYS = ["dashboard", "schedule", "clients", "invoices"];

function Brand({ dark }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="h-9 w-9 rounded-lg bg-teal-600 flex items-center justify-center"><Sparkles className="h-5 w-5 text-white" /></div>
      <span className={`font-display text-xl font-semibold tracking-tight ${dark ? "text-white" : "text-slate-900"}`}>CleanBoss</span>
    </div>
  );
}

function TrialNote({ business }) {
  if (!business?.trial_ends_at || business.plan !== "trial" || business.has_subscription) return null;
  const days = Math.max(0, Math.ceil((new Date(business.trial_ends_at) - new Date()) / 86400000));
  return <div data-testid="trial-badge" className="text-xs text-slate-400">Free trial · {days} days left</div>;
}

function AccessBanner({ business, isManager }) {
  const acc = business?.access;
  if (!acc) return null;
  const pastDue = acc.status === "past_due";
  if (acc.active && !pastDue) return null;
  const text = pastDue ? "Your last subscription payment failed. Stripe will retry - please update your payment method." : `${acc.reason} Viewing is still available; changes are paused.`;
  return (
    <div className={`mb-6 rounded-lg border p-4 text-sm flex flex-wrap items-center gap-3 ${pastDue ? "border-amber-300 bg-amber-50 text-amber-900" : "border-red-200 bg-red-50 text-red-900"}`} data-testid="access-banner">
      <AlertTriangle className="h-4 w-4" /><span className="flex-1">{text}</span>
      {isManager && <Link to="/billing" className="font-medium underline" data-testid="access-banner-billing-link">Go to Billing</Link>}
    </div>
  );
}

function SideLink({ item, onClick }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to} end={item.end} onClick={onClick} data-testid={`nav-${item.key}`}
      className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 min-h-[44px] text-sm font-medium transition-colors ${isActive ? "bg-teal-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}
    >
      <Icon className="h-[18px] w-[18px]" /> {item.label}
    </NavLink>
  );
}

export default function AppShell() {
  const { user, business, isManager, logout } = useAuth();
  const [more, setMore] = useState(false);
  const navigate = useNavigate();
  const items = NAV.filter((n) => !n.manager || isManager);
  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-slate-50 md:flex">
      <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 bg-[#0F172A] px-4 py-6" data-testid="desktop-sidebar">
        <div className="px-2 mb-8"><Brand dark /></div>
        <div className="px-2 mb-6">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Workspace</div>
          <div className="text-white font-medium truncate" data-testid="workspace-name">{business?.name}</div>
        </div>
        <nav className="flex flex-col gap-1 flex-1">{items.map((i) => <SideLink key={i.key} item={i} />)}</nav>
        <div className="border-t border-slate-800 pt-4 px-2 space-y-2">
          <div className="text-sm text-white truncate" data-testid="current-user-name">{user?.name}</div>
          <div className="text-xs text-slate-400 capitalize">{user?.role}</div>
          <TrialNote business={business} />
          <button data-testid="logout-btn" onClick={doLogout} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white min-h-[44px] transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <header className="md:hidden sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 h-14 flex items-center justify-between no-print">
        <Brand />
        <span className="text-xs text-slate-500 truncate max-w-[45%]">{business?.name}</span>
      </header>

      <main className="flex-1 md:ml-64 pb-24 md:pb-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-6 md:pt-10"><AccessBanner business={business} isManager={isManager} /><Outlet /></div>
      </main>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur-md border-t border-slate-200 grid grid-cols-5 no-print" data-testid="mobile-bottom-nav">
        {items.filter((i) => MOBILE_KEYS.includes(i.key)).map((i) => (
          <NavLink key={i.key} to={i.to} end={i.end} data-testid={`mobile-nav-${i.key}`}
            className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 min-h-[60px] text-[11px] font-medium ${isActive ? "text-teal-700" : "text-slate-500"}`}>
            <i.icon className="h-5 w-5" /> {i.label}
          </NavLink>
        ))}
        <button data-testid="mobile-nav-more" onClick={() => setMore(true)} className="flex flex-col items-center justify-center gap-0.5 min-h-[60px] text-[11px] font-medium text-slate-500">
          <Menu className="h-5 w-5" /> More
        </button>
      </nav>

      <Sheet open={more} onOpenChange={setMore}>
        <SheetContent side="left" className="bg-[#0F172A] border-slate-800 w-72">
          <SheetHeader><SheetTitle className="text-left"><Brand dark /></SheetTitle></SheetHeader>
          <nav className="flex flex-col gap-1 mt-6">{items.map((i) => <SideLink key={i.key} item={i} onClick={() => setMore(false)} />)}</nav>
          <div className="mt-6 px-3 space-y-1">
            <TrialNote business={business} />
            <button data-testid="mobile-logout-btn" onClick={doLogout} className="flex items-center gap-2 text-sm text-slate-300 min-h-[44px]"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
